import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { components } from "@jbnl/api-client";
import i18n from "@/i18n";
import { api } from "@/lib/api";

export const inschrijfperiodenKey = (seizoenId: string) =>
  ["beheer", "inschrijfperiodes", seizoenId] as const;

export const inschrijfperiodenPagedKey = (
  seizoenId: string,
  page: number | undefined,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
  targetGroup: Doelgroep | undefined,
  isOpen: boolean | undefined,
) =>
  [
    ...inschrijfperiodenKey(seizoenId),
    "paged",
    page,
    search,
    sortBy,
    sortDescending,
    targetGroup,
    isOpen,
  ] as const;

export type Inschrijfperiode = components["schemas"]["InschrijfperiodeDto"];

/** The API serialises `TargetGroup` as a number, so the SPA names the two digits once. */
export const DOELGROEP = { korpsen: 1, ondersteuners: 2 } as const;

export type Doelgroep = (typeof DOELGROEP)[keyof typeof DOELGROEP];

export function doelgroepLabel(targetGroup: number) {
  return targetGroup === DOELGROEP.korpsen
    ? i18n.t("inschrijfperiodes:doelgroep.korpsen")
    : i18n.t("inschrijfperiodes:doelgroep.ondersteuners");
}

/**
 * The API answers RFC 7807, so a refusal carries a Dutch sentence the beheerder can act on.
 * The status code is the fallback for the cases that never reach the handler.
 */
function problem(error: unknown, response: Response, fallback: string) {
  const title = (error as { title?: string } | undefined)?.title;
  return new Error(title ?? `${fallback} (${response.status}).`);
}

/** The Inschrijfperiodes list page's own read: one page, searched, filtered and sorted on the server. */
export function useInschrijfperiodes(
  seizoenId: string | undefined,
  page: number | undefined,
  pageSize: number,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
  targetGroup: Doelgroep | undefined,
  isOpen: boolean | undefined,
) {
  return useQuery({
    queryKey: inschrijfperiodenPagedKey(
      seizoenId ?? "",
      page,
      search,
      sortBy,
      sortDescending,
      targetGroup,
      isOpen,
    ),
    queryFn: !seizoenId
      ? skipToken
      : async () => {
          const { data, error, response } = await api.GET(
            "/api/beheer/seizoenen/{seizoenId}/inschrijfperiodes",
            {
              params: {
                path: { seizoenId },
                query: {
                  page,
                  pageSize,
                  search: search || undefined,
                  sortBy,
                  sortDescending,
                  targetGroup,
                  isOpen,
                },
              },
            },
          );
          if (!data) throw problem(error, response, i18n.t("inschrijfperiodes:errors.ophalen"));
          return data;
        },
    // Each page is its own query key, so without this a click to a page never fetched before
    // clears `data` and swaps the table for a skeleton - unmounting the very pager button just
    // clicked, which loses focus and (in some browsers) scrolls the page back to the top. The
    // previous page's rows stay on screen, correct but stale, until the new ones arrive.
    placeholderData: keepPreviousData,
  });
}

export type PeriodeVelden = {
  name: string;
  targetGroup: Doelgroep;
  opensOn: string;
  closesOn: string;
  wedstrijddagIds: string[];
  klasseIds: string[];
  voorkeuren: VoorkeurSettings;
};

/**
 * Toggles and counts rather than two numbers, so a count survives being turned off. The server
 * resolves the pair before the Aanmeldsite sees it; this shape exists only where both are edited.
 */
export type VoorkeurSettings = {
  voorkeurenEnabled: boolean;
  geenVoorkeurEnabled: boolean;
  maxVoorkeuren: number;
  maxGeenVoorkeuren: number;
};

/** What a korps periode asked before a beheerder could say otherwise. */
export const DEFAULT_VOORKEUR_SETTINGS: VoorkeurSettings = {
  voorkeurenEnabled: true,
  geenVoorkeurEnabled: true,
  maxVoorkeuren: 2,
  maxGeenVoorkeuren: 1,
};

/** An ondersteuner periode carries this and may carry nothing else. */
export const NO_VOORKEUR_SETTINGS: VoorkeurSettings = {
  voorkeurenEnabled: false,
  geenVoorkeurEnabled: false,
  maxVoorkeuren: 0,
  maxGeenVoorkeuren: 0,
};

/** The ceiling the server enforces, so the number inputs cannot offer more. */
export const VOORKEUR_LIMIT = 5;

export function useCreateInschrijfperiode(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: PeriodeVelden) => {
      const { data, error, response } = await api.POST(
        "/api/beheer/seizoenen/{seizoenId}/inschrijfperiodes",
        { params: { path: { seizoenId } }, body },
      );
      if (!data) throw problem(error, response, i18n.t("inschrijfperiodes:errors.aanmaken"));
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: inschrijfperiodenKey(seizoenId) }),
  });
}

export function useUpdateInschrijfperiode(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...body }: PeriodeVelden & { id: string }) => {
      const { data, error, response } = await api.PUT("/api/beheer/inschrijfperiodes/{id}", {
        params: { path: { id } },
        body,
      });
      if (!data) throw problem(error, response, i18n.t("inschrijfperiodes:errors.opslaan"));
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: inschrijfperiodenKey(seizoenId) }),
  });
}

export function useDeleteInschrijfperiode(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE("/api/beheer/inschrijfperiodes/{id}", {
        params: { path: { id } },
      });
      if (!response.ok) throw problem(error, response, i18n.t("inschrijfperiodes:errors.verwijderen"));
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: inschrijfperiodenKey(seizoenId) }),
  });
}

// ---------------------------------------------------------------- §3.3 aankondigen

export type Aankondiging = components["schemas"]["InschrijfperiodeAnnouncementDto"];
export type Ontvanger = components["schemas"]["AnnouncementRecipientDto"];

export const aankondigingKey = (periodeId: string) =>
  ["beheer", "inschrijfperiode", periodeId, "aankondiging"] as const;

/**
 * Only the three that mean it did not arrive are named: a list of two hundred delivered mails
 * answers no question anybody has.
 */
export const MAIL_STATUS = {
  queued: 1,
  sent: 2,
  failed: 3,
  delivered: 4,
  bounced: 5,
  complained: 6,
} as const;

export function mailStatusLabel(status: number) {
  switch (status) {
    case MAIL_STATUS.queued:
      return i18n.t("inschrijfperiodes:mailStatus.queued");
    case MAIL_STATUS.sent:
      return i18n.t("inschrijfperiodes:mailStatus.sent");
    case MAIL_STATUS.failed:
      return i18n.t("inschrijfperiodes:mailStatus.failed");
    case MAIL_STATUS.delivered:
      return i18n.t("inschrijfperiodes:mailStatus.delivered");
    case MAIL_STATUS.bounced:
      return i18n.t("inschrijfperiodes:mailStatus.bounced");
    case MAIL_STATUS.complained:
      return i18n.t("inschrijfperiodes:mailStatus.complained");
    default:
      return i18n.t("inschrijfperiodes:mailStatus.onbekend");
  }
}

/**
 * Deliberately not cached long: the count is also the cheapest check that the Portaal sync
 * worked, and a stale two hundred is a worse answer than a slow one.
 */
export function useAankondiging(periodeId: string) {
  return useQuery({
    queryKey: aankondigingKey(periodeId),
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/beheer/inschrijfperiodes/{id}/aankondiging",
        { params: { path: { id: periodeId } } },
      );
      if (!data) throw problem(error, response, i18n.t("inschrijfperiodes:errors.aankondigingOphalen"));
      return data;
    },
  });
}

export function useAankondigen(seizoenId: string, periodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.POST(
        "/api/beheer/inschrijfperiodes/{id}/aankondiging",
        { params: { path: { id: periodeId } } },
      );
      if (!data) throw problem(error, response, i18n.t("inschrijfperiodes:errors.aankondigen"));
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: aankondigingKey(periodeId) });
      // The list carries announcedAt too, so the badge beside the name follows.
      void queryClient.invalidateQueries({ queryKey: inschrijfperiodenKey(seizoenId) });
    },
  });
}

export function useAankondigingIntrekken(seizoenId: string, periodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error, response } = await api.DELETE(
        "/api/beheer/inschrijfperiodes/{id}/aankondiging",
        { params: { path: { id: periodeId } } },
      );
      if (!response.ok) throw problem(error, response, i18n.t("inschrijfperiodes:errors.intrekken"));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: aankondigingKey(periodeId) });
      void queryClient.invalidateQueries({ queryKey: inschrijfperiodenKey(seizoenId) });
    },
  });
}

export const ontvangersKey = (periodeId: string) =>
  ["beheer", "inschrijfperiode", periodeId, "ontvangers"] as const;

/** Its own query: 165 rows against the aankondiging card's handful, read only when opened. */
export function useOntvangers(periodeId: string, enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ontvangersKey(periodeId),
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/beheer/inschrijfperiodes/{id}/aankondiging/ontvangers",
        { params: { path: { id: periodeId } } },
      );
      if (!data) throw problem(error, response, i18n.t("inschrijfperiodes:errors.ontvangersOphalen"));
      return data;
    },
  });
}

export function useHerinnering(seizoenId: string, periodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recipients: string[]) => {
      const { data, error, response } = await api.POST(
        "/api/beheer/inschrijfperiodes/{id}/aankondiging/herinnering",
        { params: { path: { id: periodeId } }, body: { recipients } },
      );
      if (!data) throw problem(error, response, i18n.t("inschrijfperiodes:errors.herinneren"));
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: aankondigingKey(periodeId) });
      void queryClient.invalidateQueries({ queryKey: ontvangersKey(periodeId) });
      // The stamp does not move, so the list needs no refresh - but the counts on this card do.
      void queryClient.invalidateQueries({ queryKey: inschrijfperiodenKey(seizoenId) });
    },
  });
}
